import { useState, type FormEvent } from 'react';
import Reveal from '@/components/feature/Reveal';

const FORM_URL = 'https://readdy.ai/api/form/dahuvm3oh653ivfvo65g';

type Status = { type: 'success' | 'error'; message: string } | null;

export default function Contact() {
  const [status, setStatus] = useState<Status>(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string) ?? '';
    if (honeypot.trim() !== '') {
      setStatus({ type: 'success', message: 'Thank you — your message has been received.' });
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setSending(true);
    setStatus(null);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const message =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = /spam|form data is spam/i.test(message);

      if (res.ok && code === 'OK' && !isSpam) {
        setStatus({ type: 'success', message: 'Thank you — your message has been received.' });
        form.reset();
      } else {
        setStatus({
          type: 'error',
          message: message || 'Something went wrong. Please try again.',
        });
      }
    } catch {
      setStatus({ type: 'error', message: 'Unable to send your message. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: 'ri-user-line', label: 'Vinnie Cilurzo', sub: 'Owner', href: undefined },
    { icon: 'ri-mail-line', label: 'vinnie@23bottlesofbeer.com', sub: 'Email', href: 'mailto:vinnie@23bottlesofbeer.com' },
    { icon: 'ri-phone-line', label: '(707) 575-3203', sub: 'Phone', href: 'tel:+17075753203' },
  ];

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Reveal className="max-w-2xl">
          <p className="text-xs tracking-[0.35em] uppercase text-primary-600 mb-4">Contact</p>
          <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl leading-tight text-foreground-950">
            Let&rsquo;s Talk
          </h2>
          <p className="mt-5 text-foreground-600 leading-relaxed">
            For business inquiries, partnerships, or general questions, get in touch with our team.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="flex flex-col gap-6">
              {details.map((d) => (
                <div key={d.label} className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                    <i className={`${d.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-xs tracking-wide uppercase text-foreground-400 mb-1">{d.sub}</p>
                    {d.href ? (
                      <a
                        href={d.href}
                        className="text-foreground-900 font-medium hover:text-primary-600 transition-colors"
                      >
                        {d.label}
                      </a>
                    ) : (
                      <p className="text-foreground-900 font-medium">{d.label}</p>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs tracking-wide uppercase text-foreground-400 mb-1">Address</p>
                  <address className="not-italic text-foreground-900 leading-relaxed">
                    700 Mitchell Ln<br />
                    Windsor, CA 95492-6902<br />
                    United States
                  </address>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={140} className="lg:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Your name"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="company" className="block text-sm font-medium text-foreground-800 mb-2">
                  Company
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  placeholder="Company name (optional)"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                />
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="How can we help?"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-none"
                />
              </div>

              {/* Anti-spam honeypot — do not fill */}
              <input
                type="text"
                name="website_alt"
                className="website-alt-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status && (
                <p
                  className={`mt-5 text-sm ${
                    status.type === 'success' ? 'text-secondary-700' : 'text-red-700'
                  }`}
                  role="status"
                >
                  {status.message}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="whitespace-nowrap mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-8 py-3.5 text-sm font-semibold text-background-50 hover:bg-primary-600 active:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}